import PropTypes from "prop-types";
import React, { useContext, useState, useEffect } from "react";
import UserPrefsContext from "../../context/UserPrefs/UserPrefsContext";

import ToolTip from "../shared/ToolTip/ToolTip";

import InputZipcodeForChargerCatalog from "../InputComponents/InputZipcode/InputZipcodeForChargerCatalog";
import { ValidateFiveDigitCode } from "../../utils/Helpers/USPostalCodeValidator";
import { FormattedMessage, useIntl } from "react-intl";
import SelectChargerBrandFilter from "../InputComponents/SelectChargerBrandFilter/SelectChargerBrandFilter";
import SelectInstallationCosts from "../InputComponents/SelectInstallationCosts/SelectInstallationCosts";
import SelectWifiConnectivity from "../InputComponents/SelectWifiConnectivity/SelectWifiConnectivity";
import SelectChargerType from "../InputComponents/SelectChargerType/SelectChargerType";
import SelectMinimumOutput from "../InputComponents/SelectMinimumOutput/SelectMinimumOutput";
import SelectMaximumPrice from "../InputComponents/SelectMaximumPrice/SelectMaximumPrice";
import SelectPlugType from "../InputComponents/SelectPlugType/SelectPlugType";
import SelectCordLength from "../InputComponents/SelectCordLength/SelectCordLength";
import ChooseLowIncomeProgram from "../InputComponents/ChooseLowIncomeProgram/ChooseLowIncomeProgram";

import sixTwenty from "../../client_customizations/assets/images/icons/6-20.svg";
import sixThirty from "../../client_customizations/assets/images/icons/6-30.svg";
import tenThirty from "../../client_customizations/assets/images/icons/10-30.svg";
import fourteenThirty from "../../client_customizations/assets/images/icons/14-30.svg";
import sixFifty from "../../client_customizations/assets/images/icons/6-50.svg";
import fourteenFifty from "../../client_customizations/assets/images/icons/14-50.svg";
import fourteenSixty from "../../client_customizations/assets/images/icons/14-60.svg";
import dark_i from "../../client_customizations/assets/images/icons/dark_i.svg";

import "./HomeChargersFilterControls.scss";

const HomeChargersFilterControls = ({ homeChargers, showWifiFilter = true, zipcode, userLocation }) => {
     const intl = useIntl();
     const userPrefs = useContext(UserPrefsContext);
     const [workingZipcode, setWorkingZipcode] = useState(zipcode);

     useEffect(() => {
          setWorkingZipcode(zipcode);
     }, [zipcode]);

     const formFactorFilterData = userPrefs.get("chargerFormFactorFilters");

     const socketTypeWords = {
          "6-20": sixTwenty,
          "6-30": sixThirty,
          "10-30": tenThirty,
          "14-30": fourteenThirty,
          "6-50": sixFifty,
          "14-50": fourteenFifty,
          "14-60": fourteenSixty,
     };

     const updateFormFactorFilter = (key) => {
          let newFilters = Object.assign({}, formFactorFilterData);
          newFilters[key] = !newFilters[key];

          userPrefs.set({
               chargerFormFactorFilters: newFilters,
          });
     };

     const renderedFormFactorFilters = Object.keys(formFactorFilterData).map((filterKey, i) => {
          const filterName = `${filterKey.charAt(0).toUpperCase()}${filterKey.substr(1)}`;

          const hardWiredClassName = filterName === "Hardwired" ? " hardwired-btn" : "";

          return (
               <button
                    className={
                         formFactorFilterData[filterKey]
                              ? `btn active socketType${hardWiredClassName}`
                              : `btn socketType${hardWiredClassName}`
                    }
                    key={i}
                    type="button"
                    aria-pressed={!!formFactorFilterData[filterKey]}
                    onClick={() => updateFormFactorFilter(filterKey)}
               >
                    <span className="socket-text">
                         {filterName === "Hardwired"
                              ? intl.formatMessage({ id: "homeChargers.hardwired", defaultMessage: "Hard Wired" })
                              : filterName}
                    </span>
                    {filterName === "Hardwired" ? null : (
                         <img
                              className="socket-image"
                              src={socketTypeWords[filterName]}
                              alt={
                                   filterName === "Hardwired"
                                        ? intl.formatMessage({
                                               id: "homeChargers.hardwired",
                                               defaultMessage: "Hardwired",
                                          })
                                        : filterName
                              }
                         />
                    )}
               </button>
          );
     });

     return (
          <div className="input-well ChargerFilterControls">
               <form>
                    <div className="form-group">
                         <div className="btn-block-container">
                              <InputZipcodeForChargerCatalog
                                   zipcode={userPrefs.get("zipcode")}
                                   workingZipcode={workingZipcode}
                                   userLocation={userLocation}
                                   setZipcode={(value) => {
                                        setWorkingZipcode(value);
                                        if (ValidateFiveDigitCode(value)) {
                                             userPrefs.set({ zipcode: value });
                                        }
                                   }}
                                   id="input-zipcode-for-available-incentives"
                                   isUpdating={userPrefs.zipcodeIsUpdating}
                                   isNotFound={userPrefs.zipcodeIsNotFound}
                                   showButtonInline={true}
                                   hideValidateRequest
                              />
                         </div>
                    </div>
                    <p className="h2">
                         <FormattedMessage id="evfilter" defaultMessage="Filter" description="Filter" />
                    </p>
                    <SelectInstallationCosts />
                    <SelectWifiConnectivity />
                    <SelectChargerBrandFilter chargers={homeChargers} />
                    <SelectChargerType chargers={homeChargers} />
                    <SelectMinimumOutput chargers={homeChargers} />
                    <SelectMaximumPrice chargers={homeChargers} />
                    <SelectPlugType chargers={homeChargers} />
                    <SelectCordLength chargers={homeChargers} />
                    <ChooseLowIncomeProgram />

                    <div className="form-group">
                         <span className="label-style">
                              <FormattedMessage id="homeChargers.socket" defaultMessage="Socket" description="Socket" />
                              <ToolTip
                                   message={intl.formatMessage({
                                        id: "homeChargers.socketTooltip",
                                        defaultMessage:
                                             "Different model chargers have different plug patterns. These plug patterns correspond to the way prongs fit into your wall outlet. If you're not sure which is best for your home/garage, check with a qualified electrician.",
                                   })}
                                   id="socket_tooltip"
                                   icon={dark_i}
                              />
                         </span>
                         <div className="btn-grid-container socketType-grid-container">{renderedFormFactorFilters}</div>
                    </div>
               </form>
          </div>
     );
};

export default HomeChargersFilterControls;

HomeChargersFilterControls.propTypes = {
     showWifiFilter: PropTypes.bool,
     homeChargers: PropTypes.array,
};
